import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { api } from "helpers/axios";
import Link from "components/ui/Link";
import { errorMessage, scrollInto } from "helpers/common";
import { toast } from "helpers/toast";
import { fetchFields, getFreeFields } from "store/fieldsSlice";
import * as Sentry from "@sentry/react";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { Button, Field, Input } from "components/ui";

export default function FreeFields() {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const [formErrors, setFormErrors] = useState([]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { register, getValues, setValue, watch } = useForm();

  const watchFields = watch('fields');

  const freeFields = useSelector(getFreeFields);

  useEffect(() => {
    dispatch(fetchFields());
  }, []);

  useEffect(() => {
    if(freeFields) {
      const currentDisplayOptions = {
        employee_management: [],
        balanced_scorecard: []
      };

      freeFields.forEach((f, i) => {
        setValue(`fields.${i}.hash_id`, f.hash_id);
        setValue(`fields.${i}.is_active`, f.is_active);
        setValue(`fields.${i}.name`, f.name);

        if (f.display_in.includes('employee_management')) {
          currentDisplayOptions.employee_management.push(f.hash_id);
        }
        if (f.display_in.includes('balanced_scorecard')) {
          currentDisplayOptions.balanced_scorecard.push(f.hash_id);
        }
        setValue(`display_options`, currentDisplayOptions);
      });
    }
  }, [freeFields]);

  const handleSaveFreeFiels = async () => {
    setError(null);

    const values = getValues();

    const hasError = formErrors.filter(e => e).length > 0;

    const formData = [];
    values.fields.forEach(item => {
      if (typeof item.is_active === 'string') item.is_active = (item.is_active === 'true');
      item.is_required = false;

      if (item.hash_id || item.name) formData.push(item);
    });

    if (hasError) {
      scrollInto('.settings-employee');
      return;
    }

    setLoading(true);

    try {
      const { status } = await api.post(`/settings/fields`, formData);
      if (status === 200) {
        setLoading(false);

        dispatch(fetchFields());

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err, 'save free fields'));
      scrollInto('.settings-employee');
      Sentry.captureException(err);
    }
  };

  const handleFreeFieldChange = (i) => {
    const newFormErrors = [...formErrors];
    if ([true, 'true'].includes(watchFields[i].is_active) || watchFields[i].hash_id) {
      if (!watchFields[i].name) {
        newFormErrors[i] = true;
        setFormErrors(newFormErrors);
      } else if (formErrors[i] && watchFields[i].name) {
        newFormErrors[i] = false;
        setFormErrors(newFormErrors);
      }
    } else {
      newFormErrors[i] = false;
      setFormErrors(newFormErrors);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4>{editableTranslate('global.menu.settings.free_fields')}</h4>
          <p className="section-description">{editableTranslate('free_fields.description')} <Link to="/settings/display-options" data-testid="display-options-link">{editableTranslate('global.menu.settings.display_options')}</Link>.</p>

          <div className="settings-form settings-employee">
            <div className="mt-4 mb-5">
              {
                error && <div>
                  <ErrorAlert error={error} onClose={() => setError(null)} />
                </div>
              }

              <table className="free-fields-table">
                <thead>
                  <tr>
                    <th className="text-center">{editableTranslate('free_fields.active')}</th>
                    <th>{editableTranslate('free_fields.field_name')}</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    Array.from(Array(10), (_, i) => {
                      return <tr key={i}>
                        <td className="text-center">
                          <input type="hidden" {...register(`fields.${i}.hash_id`)} />
                          <input type="checkbox" className="m-auto" value={true} {...register(`fields.${i}.is_active`, { onChange: (e) => handleFreeFieldChange(i) })} data-testid={`free-field-${i}-is-active`} />
                        </td>
                        <td>
                          <Field error={formErrors[i] && <>{freeFields && freeFields[i]?.hash_id && <span>{editableTranslate('free_fields.error_existing_field')}<br /></span>} {editableTranslate('free_fields.error_empty_field')}</>}>
                            <Input type="text" error={!!formErrors[i]} placeholder={translate('free_fields.empty_field_placeholder')} {...register(`fields.${i}.name`, { onChange: (e) => handleFreeFieldChange(i) })} maxLength={255} />
                          </Field>
                        </td>

                      </tr>
                    })
                  }
                </tbody>
              </table>
              <div className="settings-sticky-footer">
                <hr className="mt-0" />
                <Button variant="primary" onClick={handleSaveFreeFiels} loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
